import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ClipboardCopy, IdCard } from "lucide-react";
import toast from "react-hot-toast";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import CopyId from "@/components/ui/CopyId";
import { copyToClipboard } from "@/utils/clipboard";

const PANEL_GAP = 6;
const PANEL_WIDTH = 280;

/**
 * A compact "IDs" action shown alongside the candidate stage dropdown. Opens
 * a small panel (Zoho-style) listing the candidate / application / job ids,
 * each with a right-aligned copy button, plus a "copy all" action.
 *
 * The panel is portal-rendered with fixed positioning because the page is
 * built from `overflow-hidden` cards — an absolutely positioned dropdown
 * would be clipped.
 */
const IdsDropdown = ({ candidateId, applicationId, jobId }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({});

  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const ids = useMemo(
    () =>
      [
        { label: "Candidate", value: candidateId },
        { label: "Application", value: applicationId },
        { label: "Job", value: jobId },
      ].filter((item) => item.value),
    [candidateId, applicationId, jobId],
  );

  const positionPanel = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = rect.right - PANEL_WIDTH;
    if (left < 8) left = 8;
    if (left + PANEL_WIDTH > viewportWidth - 8) {
      left = viewportWidth - PANEL_WIDTH - 8;
    }

    const spaceBelow = viewportHeight - rect.bottom - PANEL_GAP;
    const spaceAbove = rect.top - PANEL_GAP;
    const openBelow = spaceBelow >= spaceAbove;

    setPosition({
      position: "fixed",
      left,
      width: PANEL_WIDTH,
      zIndex: 9999,
      top: openBelow ? rect.bottom + PANEL_GAP : undefined,
      bottom: openBelow ? undefined : viewportHeight - rect.top + PANEL_GAP,
    });
  }, []);

  useEffect(() => {
    if (open) positionPanel();
  }, [open, positionPanel]);

  useEffect(() => {
    if (!open) return;

    window.addEventListener("resize", positionPanel);
    window.addEventListener("scroll", positionPanel, true);
    return () => {
      window.removeEventListener("resize", positionPanel);
      window.removeEventListener("scroll", positionPanel, true);
    };
  }, [open, positionPanel]);

  useEffect(() => {
    const handler = (e) => {
      if (!triggerRef.current?.contains(e.target) && !panelRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const handleCopyAllIds = useCallback(async () => {
    if (ids.length === 0) return;

    const ok = await copyToClipboard(ids.map((item) => `${item.label}: ${item.value}`).join("\n"));

    if (ok) {
      toast.success("IDs copied");
    } else {
      toast.error("Could not copy IDs — copy them manually");
    }
  }, [ids]);

  if (ids.length === 0) return null;

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Copy IDs"
        title="Copy IDs"
        className={cn(
          "inline-flex h-7 shrink-0 items-center gap-1 rounded-full border border-border-primary bg-background-tertiary px-2.5 text-xs font-medium text-text-muted transition-colors",
          "hover:border-tone-blue/30 hover:bg-tone-blue-soft hover:text-tone-blue",
          open && "border-tone-blue/30 bg-tone-blue-soft text-tone-blue",
        )}
      >
        <IdCard size={13} aria-hidden="true" />
        <span>IDs</span>
        <ChevronDown size={11} className={cn("transition-transform duration-200", open && "rotate-180")} aria-hidden="true" />
      </Button>

      {open &&
        (position.top != null || position.bottom != null) &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            aria-label="Record IDs"
            style={position}
            className="overflow-hidden rounded-xl border border-border-primary bg-background-form shadow-xl"
          >
            <p className="flex items-center gap-1.5 px-3.5 pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-wide text-text-faint">
              <IdCard size={12} aria-hidden="true" />
              Record IDs
            </p>

            <div className="flex flex-col gap-0.5 px-1.5 pb-1.5">
              {ids.map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between gap-2.5 rounded-lg px-2.5 py-2 transition-colors hover:bg-background-tertiary">
                  <span className="shrink-0 text-xs font-medium text-text-secondary">{label}</span>

                  <CopyId label={label} value={value} hideLabel idClassName="text-xs" />
                </div>
              ))}
            </div>

            <div className="mx-3.5 border-t border-border-primary" />

            <Button
              onClick={handleCopyAllIds}
              className="flex w-full items-center gap-2 rounded-none px-3.5 py-2.5 text-left text-xs font-medium text-text-muted transition-colors hover:bg-background-tertiary hover:text-text-primary"
            >
              <ClipboardCopy size={12} aria-hidden="true" />
              Copy all IDs
            </Button>
          </div>,
          document.body,
        )}
    </>
  );
};

export default IdsDropdown;
